import { DateTime } from 'luxon';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { ICalendarKeys } from '../index';

import { injectQuotes } from '../../lib/utils';
import { Dividends, Earnings, AnalystRating, Options, IPO } from './index';
import {
  CalendarManager,
  CalendarEvents,
  EarningsCalendarParams,
  RatingsCalendarParams,
  IposCalendarParams,
} from '@benzinga/calendar-manager';
import { fetchDividends } from '../../DividendsCalendar/Services';
import { OptionsActivity } from '@benzinga/calendar-option-activity-manager';
import type { Session } from '@benzinga/session';

export interface IFetchParams {
  symbols?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  pageSize?: number;
}

export type CalendarTab = 'Earnings' | 'Analyst Ratings' | 'Options' | 'Dividends' | 'IPOs';

export type CalendarFetchData = Record<string, CalendarEvents[] | OptionsActivity[]>;

export interface ICalendarDef {
  key: ICalendarKeys;
  link: string;
  tab: CalendarTab;
  columns: (ColDef | ColGroupDef)[];
  edgeAdKey?: string;
  fetch(session: Session, params: IFetchParams): Promise<{ data: CalendarFetchData }>;
}

const sliceAndGroupData = <T extends { ticker: string }>(items: T[], dateField = 'date', sort = 'asc'): T[] => {
  const groupBy = (xs: T[], key: keyof T) => {
    return xs.reduce(
      (rv, x) => {
        const val = x[key] as unknown as string;
        (rv[val] = rv[val] || []).push(x);
        return rv;
      },
      {} as Record<string, T[]>,
    );
  };

  let sortByDate = (a: T, b: T) => {
    const dateA = DateTime.fromISO(a[dateField]);
    const dateB = DateTime.fromISO(b[dateField]);
    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;
    return 0;
  };

  if (sort === 'desc') {
    sortByDate = (a: T, b: T) => {
      const dateA = DateTime.fromISO(a[dateField]);
      const dateB = DateTime.fromISO(b[dateField]);
      if (dateA > dateB) return -1;
      if (dateA < dateB) return 1;
      return 0;
    };
  }

  const grouped = groupBy(items, 'ticker' as keyof T);
  const separated: Record<string, { future: T[]; past: T[] }> = {};
  let res: T[] = [];

  for (const ticker in grouped) {
    separated[ticker] = {
      future: [],
      past: [],
    };
    grouped[ticker].forEach((el: T) => {
      const date = DateTime.fromISO(el[dateField]).startOf('day');
      if (date < DateTime.now().startOf('day')) {
        separated[ticker].past.push(el);
      } else {
        separated[ticker].future.push(el);
      }
    });

    const pastSliced = separated[ticker].past.slice(0, 3);
    const futureSliced = separated[ticker].future.slice(0, 2);
    res = res.concat([...pastSliced, ...futureSliced]);
  }

  return res.sort(sortByDate);
};

const CalendarDef: ICalendarDef[] = [
  {
    columns: Earnings,
    edgeAdKey: 'earnings',
    fetch: (session: Session, params: IFetchParams = {}) => {
      const calendarManager = session.getManager(CalendarManager);
      const query: EarningsCalendarParams = {
        dateFrom: params.dateFrom ?? DateTime.now().minus({ months: 1 }).toJSDate(),
        dateTo: params.dateTo ?? new Date(),
        pageSize: params.pageSize,
        symbols: params.symbols,
      };

      return calendarManager.getCalendarData('earnings', query).then(res => {
        const response: { data: { earnings: CalendarEvents[] } } = {
          data: {
            earnings: [],
          },
        };

        if (res.ok && res.ok.length) {
          response.data.earnings = sliceAndGroupData(res.ok);
          const sortByRevenueEst = (a: any, b: any) =>
            b.date < a.date ? -1 : 1 || (Number(a.revenue_est ?? 0) < Number(b.revenue_est ?? 0) ? 1 : -1);
          response.data.earnings.sort(sortByRevenueEst);
          return response;
        }
        return response;
      });
    },
    key: 'earnings',
    link: 'https://www.benzinga.com/earnings',
    tab: `Earnings`,
  },
  {
    columns: AnalystRating,
    edgeAdKey: 'analyst-ratings',
    fetch: (session: Session, params: IFetchParams = {}) => {
      const query: RatingsCalendarParams = {
        dateFrom: DateTime.now().minus({ months: 6 }).toJSDate(),
        dateTo: new Date(),
        pageSize: params.pageSize,
        symbols: params.symbols,
      };
      const calendarManager = session.getManager(CalendarManager);
      return calendarManager.getCalendarData('ratings', query).then(res => {
        const ratings = res.ok;

        const response: { data: { ratings: CalendarEvents[] } } = {
          data: {
            ratings: [],
          },
        };

        if (Array.isArray(ratings) && ratings.length) {
          return injectQuotes(ratings, undefined, session).then(res => {
            response.data.ratings = sliceAndGroupData(res, 'date', 'desc');
            return response;
          });
          // This is for if we can get await to work via babel update
          // transformedData = await injectQuotes(transformedData);
          // response.data.ratings = transformedData;
          // return response;
        }
        return response;
      });
    },
    key: 'ratings',
    link: 'https://www.benzinga.com/analyst-stock-ratings',
    tab: `Analyst Ratings`,
  },
  {
    columns: Options,
    edgeAdKey: 'unusual-options-activity',
    fetch: (session: Session, params: IFetchParams = {}) => {
      const calendarManager = session.getManager(CalendarManager);
      const dateFrom = DateTime.now().minus({ months: 1 });
      const dateTo = DateTime.now().plus({ months: 1 });
      const query = {
        dateFrom: dateFrom.toISODate() ?? dateFrom.toISO(),
        dateTo: dateTo.toISODate() ?? dateTo.toISO(),
        pageSize: params.pageSize,
        symbols: params.symbols,
      };

      return calendarManager.getCalendarData('optionsActivity', query).then(res => {
        const response: { data: CalendarFetchData } = {
          data: { option_activity: [] },
        };

        if (res.ok && res.ok.length) {
          response.data.option_activity = sliceAndGroupData(res.ok as OptionsActivity[]);
          return response;
        }
        return response;
      });
    },
    key: 'option_activity',
    link: 'https://www.benzinga.com/calendars/options',
    tab: `Options`,
  },
  {
    columns: Dividends,
    edgeAdKey: 'basic-calendar',
    fetch: (session: Session, params: IFetchParams = {}) => {
      return fetchDividends(
        session,
        {
          dateFrom: DateTime.now().toFormat('yyyy-MM-dd'),
          dateSort: 'ex',
          dateTo: params.symbols?.length
            ? DateTime.now().plus({ months: 3 }).toFormat('yyyy-MM-dd')
            : DateTime.now().toFormat('yyyy-MM-dd'),
          pageSize: params.pageSize,
          symbols: params.symbols,
        },
        false,
        params.pageSize,
      ).then(items => {
        const response: { data: { dividends: CalendarEvents[] } } = {
          data: {
            dividends: [],
          },
        };

        if (items.length) {
          response.data.dividends = sliceAndGroupData(items, 'ex_dividend_date');
        }
        return response;
      });
    },
    key: 'dividends',
    link: 'https://www.benzinga.com/dividends',
    tab: `Dividends`,
  },
  {
    columns: IPO,
    edgeAdKey: 'basic-calendar',
    fetch: (session: Session, params: IFetchParams = {}) => {
      const calendarManager = session.getManager(CalendarManager);
      const query: IposCalendarParams = {
        dateFrom: new Date(),
        dateTo: DateTime.now().plus({ months: 1 }).toJSDate(),
        pageSize: params.pageSize,
        symbols: params.symbols,
      };
      return calendarManager.getCalendarData('ipos', query).then(res => {
        const response: { data: { ipos: CalendarEvents[] } } = {
          data: {
            ipos: [],
          },
        };

        if (res.ok && res.ok.length) {
          response.data.ipos = sliceAndGroupData(res.ok);
          return response;
        }
        return response;
      });
    },
    key: 'ipos',
    link: 'https://www.benzinga.com/calendars/ipos',
    tab: `IPOs`,
  },
  // {
  //   columns: Economics,
  //   fetch: () => Calendar.getEconomics(setParams({ dateFrom: subDays(new Date(), 7), dateTo: new Date() })),
  //   key: 'economics',
  //   link: 'https://www.benzinga.com/calendars/economic',
  //   tab: `Economic`,
  // },
  // {
  //   columns: StockSplit,
  //   fetch: (params: IFetchParams = {}) =>
  //     Calendar.getSplits(
  //       setParams({
  //         dateFrom: new Date(),
  //         dateTo: addMonths(new Date(), 1),
  //         ...params,
  //         addParams: { key: 'parameters[date_sort]', val: 'ex' },
  //       }),
  //     ),
  //   key: 'splits',
  //   link: 'https://www.benzinga.com/calendars/stock-splits',
  //   tab: `Stock Split`,
  // },
  // {
  //   columns: ConferenceCall,
  //   fetch: (params: IFetchParams = {}) =>
  //     Calendar.getConferenceCalls(setParams({ dateFrom: new Date(), dateTo: addMonths(new Date(), 1), ...params })),
  //   key: 'conference',
  //   link: 'https://www.benzinga.com/calendars/conference-calls',
  //   tab: `Conference Call`,
  // },
  // {
  //   columns: MA,
  //   fetch: (params: IFetchParams = {}) =>
  //     Calendar.getMA(setParams({ dateFrom: subMonths(new Date(), 2), dateTo: new Date(), ...params })),
  //   key: 'ma',
  //   link: 'https://www.benzinga.com/calendars/m-a',
  //   tab: `M&A`,
  // },
];

export default CalendarDef;
